import { ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, inject, input, output, signal } from '@angular/core';

// T = tipo del id de cada opción. Number por default (Provincia/Localidad);
// Cobertura no tiene id propio — el nombre es la clave (ver
// docs/specs/abmc-notificacion-institucional.md corrección #8) — usa T=string.
export interface MultiSelectOpcion<T extends string | number = number> {
  id: T;
  nombre: string;
}

export interface MultiSelectCambio<T extends string | number = number> {
  todos: boolean;
  ids: T[];
}

// Componente presentacional: input tipo typeahead (foco = abre) con un
// checklist multi-select debajo, filtrable escribiendo.
// "Todos" es un estado explícito (input `todos`), no inferido de la
// selección — ver docs/specs/abmc-notificacion-institucional.md §9.9: antes
// "Todos" y "destildé todo sin elegir nada" compartían el mismo valor ([]),
// lo que los hacía indistinguibles.
@Component({
  selector: 'app-multi-select-dropdown',
  standalone: true,
  templateUrl: './multi-select-dropdown.component.html',
  styleUrl: './multi-select-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectDropdownComponent<T extends string | number = number> {
  private static contador = 0;
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly opciones = input.required<MultiSelectOpcion<T>[]>();
  readonly seleccionados = input.required<T[]>();
  readonly todos = input.required<boolean>();
  readonly label = input<string>('');
  readonly deshabilitado = input(false);
  readonly cambio = output<MultiSelectCambio<T>>();

  // ponytail: ids únicos por instancia solo para no duplicar `id`/`for` en el
  // DOM cuando hay más de un dropdown en la misma pantalla (Cobertura + Rango
  // etario) — no hace falta nada más sofisticado que un contador estático.
  readonly idBase = `msd-${MultiSelectDropdownComponent.contador++}`;

  readonly abierto = signal(false);
  // Nombres en vez de "N seleccionados" — para que se sepa qué está tildado
  // sin tener que abrir el combo. Con muchas opciones elegidas, corta a las
  // primeras 3 + contador (si no, el input se llena de texto ilegible).
  readonly textoResumen = computed(() => {
    if (this.todos()) return 'Todos';
    const ids = this.seleccionados();
    if (ids.length === 0) return 'Ninguna seleccionada';
    const opciones = this.opciones();
    const nombres = ids
      .map(id => opciones.find(o => o.id === id)?.nombre)
      .filter((nombre): nombre is string => nombre !== undefined);
    if (nombres.length <= 3) return nombres.join(', ');
    return `${nombres.slice(0, 3).join(', ')} y ${nombres.length - 3} más`;
  });

  readonly busqueda = signal('');
  readonly opcionesFiltradas = computed(() => {
    const q = this.busqueda().trim().toLowerCase();
    if (!q) return this.opciones();
    return this.opciones().filter(o => o.nombre.toLowerCase().includes(q));
  });

  abrir(): void {
    if (this.deshabilitado()) return;
    this.abierto.set(true);
  }

  cerrar(): void {
    this.abierto.set(false);
    this.busqueda.set('');
  }

  onTodosChange(marcado: boolean): void {
    if (marcado) this.cambio.emit({ todos: true, ids: [] });
  }

  onOpcionToggle(id: T, marcado: boolean): void {
    const actuales = this.seleccionados();
    const ids = marcado ? [...actuales, id] : actuales.filter(i => i !== id);
    this.cambio.emit({ todos: false, ids });
  }

  // Cierre al click afuera: listener simple en document + chequeo de
  // contains(), sin librería nueva (CDK Overlay sería de más acá).
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.abierto()) return;
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.cerrar();
    }
  }
}
